import { useTranslation } from '@/src/i18n';
import { KeyboardAvoidingView, Platform, ScrollView, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from './EditTournamentScreen.styles';

export interface EditTournamentScreenProps {
  name: string;
  setName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  maxParticipants: number | undefined;
  setMaxParticipants: (value: number | undefined) => void;
  saving: boolean;
  handleSave: () => void;
  handleCancel: () => void;
  colors: {
    background: string;
    card: string;
    border: string;
    text: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    success: string;
  };
}

export const EditTournamentScreen = ({
  name,
  setName,
  description,
  setDescription,
  maxParticipants,
  setMaxParticipants,
  saving,
  handleSave,
  handleCancel,
  colors,
}: EditTournamentScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const PARTICIPANT_LIMITS = [
    { value: undefined, label: t('tournaments.unlimited') },
    { value: 4, label: '4' },
    { value: 8, label: '8' },
    { value: 16, label: '16' },
    { value: 32, label: '32' },
    { value: 64, label: '64' },
  ];

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>{t('tournaments.tournamentName')} {t('tournaments.required')}</Text>
          <TextInput
            style={[styles.input, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
            placeholder={t('tournaments.enterName')}
            placeholderTextColor={colors.textTertiary}
            value={name}
            onChangeText={setName}
            autoFocus
          />
        </View>
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>{t('tournaments.descriptionOptional')}</Text>
          <TextInput
            style={[styles.input, styles.textArea, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
            placeholder={t('tournaments.enterDescription')}
            placeholderTextColor={colors.textTertiary}
            value={description}
            onChangeText={setDescription}
            multiline
            numberOfLines={3}
          />
        </View>
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>{t('tournaments.maxParticipants')}</Text>
          <Text style={[styles.labelHint, { color: colors.textTertiary }]}>
            {t('tournaments.maxParticipantsHint')}
          </Text>
          <View style={styles.limitOptions}>
            {PARTICIPANT_LIMITS.map((option) => (
              <TouchableOpacity
                key={option.label}
                style={[
                  styles.limitOption,
                  {
                    backgroundColor: maxParticipants === option.value ? colors.primary : colors.card,
                    borderColor: maxParticipants === option.value ? colors.primary : colors.border,
                  },
                ]}
                onPress={() => setMaxParticipants(option.value)}
              >
                <Text
                  style={[
                    styles.limitOptionText,
                    { color: maxParticipants === option.value ? colors.primaryText : colors.text },
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        <TouchableOpacity
          style={[styles.saveBtn, { backgroundColor: saving ? colors.border : colors.success }]}
          onPress={handleSave}
          disabled={saving}
        >
          <Text style={[styles.saveBtnText, { color: colors.primaryText }]}>
            {saving ? t('editProfile.saving') : t('editProfile.saveChanges')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel} disabled={saving}>
          <Text style={[styles.cancelBtnText, { color: colors.primary }]}>{t('common.cancel')}</Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};
